import type { ChipColor } from '@/types/chip';
import { Box, Typography, Paper, Grid, Chip, IconButton, Divider, Link, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { Event, Close, Person, CalendarMonth } from '@mui/icons-material';
import { PageDescription } from '@/components/help';
import { Show, useGetList } from 'react-admin';
import { useNavigate } from 'react-router-dom';

interface TutoringSessionRecord {
  id: string;
  startDateTime: string;
  endDateTime: string;
  status: string;
  students?: Array<{ type: string; id: string }>;
}

const STATUS_COLORS: Record<string, ChipColor> = {
  Draft: 'default',
  Proposed: 'info',
  Accepted: 'success',
  Rejected: 'error',
  Active: 'success',
  Completed: 'secondary',
  Cancelled: 'warning',
};

const SESSION_STATUS_COLORS: Record<string, ChipColor> = {
  Draft: 'default',
  Scheduled: 'info',
  Confirmed: 'success',
  Completed: 'secondary',
  Cancelled: 'error',
  Rescheduled: 'warning',
};

export const TutoringScheduleDetail = () => {
  const navigate = useNavigate();

  return (
    <Show render={({ record }) => {
      if (!record) return null;

      return (
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <IconButton onClick={() => navigate('/tutoring-schedules')} size="small" sx={{ color: 'text.secondary' }} aria-label="Close">
              <Close />
            </IconButton>
          </Box>

          <PageDescription
            entity="Tutoring Schedule"
            description="A recurring session pattern linked to an agreement. Defines the day, time, and subject for sessions."
            icon={<Event />}
          />

          <Paper elevation={0} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #00695c, #00897b)', color: 'primary.contrastText', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Event sx={{ fontSize: 48 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" fontWeight={700}>{record.tutorName || 'Schedule'}</Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{record.subject || '—'} · {record.clientName || '—'}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip label={record.status} size="small" color={STATUS_COLORS[record.status]} />
                </Box>
              </Box>
            </Box>
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Event color="primary" />
              <Typography variant="subtitle1" fontWeight={700}>Schedule Details</Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="caption" color="text.secondary">Subject</Typography>
                <Typography variant="body1" fontWeight={500}>{record.subject || '—'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="caption" color="text.secondary">Tutor</Typography>
                <Typography variant="body1">
                  {record.tutor?.id ? (
                    <Link
                      href={`/tutors/${record.tutor.id}/show`}
                      underline="hover"
                      sx={{ color: 'primary.main', cursor: 'pointer' }}
                    >
                      {record.tutorName || '—'}
                    </Link>
                  ) : (record.tutorName || '—')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="caption" color="text.secondary">Client</Typography>
                <Typography variant="body1">
                  {record.agreement?.id ? (
                    <Link
                      href={`/clients/${record.agreement.id}/show`}
                      underline="hover"
                      sx={{ color: 'primary.main', cursor: 'pointer' }}
                    >
                      {record.clientName || '—'}
                    </Link>
                  ) : (record.clientName || '—')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Typography variant="body1">{record.status || '—'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="caption" color="text.secondary">Agreement</Typography>
                <Typography variant="body1">
                  {record.agreement?.id ? (
                    <Link
                      href={`/tutoring-agreements/${record.agreement.id}/show`}
                      underline="hover"
                      sx={{ color: 'primary.main', cursor: 'pointer' }}
                    >
                      {record.agreement.id.split(':').pop()?.substring(0, 12) || record.agreement.id}
                    </Link>
                  ) : '—'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="caption" color="text.secondary">Created</Typography>
                <Typography variant="body1">{record.metadata?.createdAt ? new Date(record.metadata.createdAt).toLocaleDateString() : '—'}</Typography>
              </Grid>
            </Grid>
          </Paper>

          <ScheduleSessionsSection scheduleId={record.id} />
        </Box>
      );
    }} />
  );
};

const ScheduleSessionsSection = ({ scheduleId }: { scheduleId: string }) => {
  const navigate = useNavigate();
  const { data: sessions, isLoading } = useGetList('tutoring-sessions', {
    filter: { scheduleId },
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'startDateTime', order: 'ASC' },
  });

  const sessionList = sessions || [];

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <CalendarMonth color="primary" />
        <Typography variant="subtitle1" fontWeight={700}>Sessions ({sessionList.length})</Typography>
      </Box>
      {isLoading ? (
        <Typography variant="body2" color="text.secondary">Loading sessions...</Typography>
      ) : sessionList.length > 0 ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Time</TableCell>
              <TableCell>Students</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sessionList.map((session: TutoringSessionRecord) => (
              <TableRow
                key={session.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/tutoring-sessions/${session.id}/show`)}
              >
                <TableCell>
                  {session.startDateTime ? new Date(session.startDateTime).toLocaleDateString() : '—'}
                </TableCell>
                <TableCell>
                  {session.startDateTime ? new Date(session.startDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Person fontSize="small" color="action" />
                    <Typography variant="body2">{session.students?.length || 0}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={session.status} size="small" color={SESSION_STATUS_COLORS[session.status]} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" color="text.secondary">No sessions yet</Typography>
      )}
    </Paper>
  );
};
